import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../config/api';  // ✅ CORRECT - gets api object

const TeamManagement = () => {
  const { token, user } = useAuth();
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      fetchTeamData();
    }
  }, [token]);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const teamData = await api.getCurrentTeam(token);
      setTeam(teamData);
      
      if (teamData?.id) {
        const [membersData, invitationsData] = await Promise.all([
          api.getTeamMembers(token, teamData.id),
          api.getTeamInvitations(token, teamData.id)
        ]);
        setMembers(Array.isArray(membersData) ? membersData : []);
        setInvitations(Array.isArray(invitationsData) ? invitationsData : []);
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      await api.inviteTeamMember(token, team.id, inviteEmail, inviteRole);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
      await fetchTeamData();
      alert('Invitation sent successfully!');
    } catch (error) {
      setError(error.message || 'Failed to send invitation');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    if (!window.confirm('Are you sure you want to change this member\'s role?')) return;
    
    try {
      await api.updateTeamMemberRole(token, team.id, userId, newRole);
      await fetchTeamData();
      alert('Role updated successfully!');
    } catch (error) {
      alert('Failed to update role: ' + error.message);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member from the team?')) return;
    
    try {
      await api.removeTeamMember(token, team.id, userId);
      await fetchTeamData();
      alert('Member removed successfully!');
    } catch (error) {
      alert('Failed to remove member: ' + error.message);
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) return;
    
    try {
      await api.deleteInvitation(token, team.id, invitationId);
      await fetchTeamData();
      alert('Invitation cancelled successfully!');
    } catch (error) {
      alert('Failed to cancel invitation: ' + error.message);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'admin': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'member': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'viewer': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const canManageMembers = team?.user_role === 'owner' || team?.user_role === 'admin';

  if (loading) {
    return (
      <div className="min-h-screen bg-cyber-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading team data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cyber-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Invite Button */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Team Management</h1>
            <p className="text-gray-400 mt-1">Manage your team members and permissions</p>
          </div>
          {canManageMembers && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 bg-cyber-cyan text-cyber-darker font-semibold rounded-lg hover:bg-cyan-400 transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Invite Member</span>
            </button>
          )}
        </div>

        {/* Team Information Card */}
        {team && (
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6 mb-8">
            <h3 className="text-xl font-semibold text-white mb-4">Team Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="text-sm text-gray-400">Team Name</label>
                <p className="text-white font-medium text-lg">{team.name}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Your Role</label>
                <div className="mt-1">
                  <span className={`inline-block px-3 py-1 text-sm rounded border ${getRoleBadgeColor(team.user_role)}`}>
                    {team.user_role}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400">Total Members</label>
                <p className="text-white font-medium text-lg">{team.member_count}</p>
              </div>
            </div>
          </div>
        )}

        {/* Team Members List */}
        <div className="bg-cyber-card border border-gray-800 rounded-lg p-6 mb-8">
          <h3 className="text-xl font-semibold text-white mb-4">Team Members ({members.length})</h3>
          <div className="space-y-4">
            {members.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No team members found</p>
            ) : (
              members.map((member) => (
                <div
                  key={member.id}
                  className="bg-cyber-darker border border-gray-700 rounded-lg p-4 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-cyber-cyan/20 rounded-full flex items-center justify-center">
                        <span className="text-cyber-cyan font-semibold">
                          {member.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <h4 className="text-white font-medium">{member.name}</h4>
                        <p className="text-sm text-gray-400">{member.email}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    {canManageMembers && member.role !== 'owner' ? (
                      <select
                        value={member.role}
                        onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                        className="px-3 py-1 bg-cyber-darker border border-gray-700 rounded text-white text-sm focus:border-cyber-cyan focus:outline-none"
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    ) : (
                      <span className={`px-3 py-1 text-sm rounded border ${getRoleBadgeColor(member.role)}`}>
                        {member.role}
                      </span>
                    )}

                    {canManageMembers && member.role !== 'owner' && (
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="px-3 py-1 bg-red-500/20 text-red-400 text-sm rounded border border-red-500/30 hover:bg-red-500/30 transition-colors"
                      >
                        Remove
                      </button>
                    )}

                    {member.role === 'owner' && (
                      <span className="text-xs text-gray-500 px-3">Owner - Cannot be removed</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pending Invitations */}
        {canManageMembers && invitations.filter(i => i.status === 'pending').length > 0 && (
          <div className="bg-cyber-card border border-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4">
              Pending Invitations ({invitations.filter(i => i.status === 'pending').length})
            </h3>
            <div className="space-y-4">
              {invitations.filter(i => i.status === 'pending').map((invitation) => (
                <div
                  key={invitation.id}
                  className="bg-cyber-darker border border-gray-700 rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <h4 className="text-white font-medium">{invitation.email}</h4>
                    <p className="text-sm text-gray-400">
                      Invited {new Date(invitation.created_at).toLocaleDateString()} by {invitation.invited_by_name}
                    </p>
                  </div>

                  <div className="flex items-center space-x-4">
                    <span className={`px-3 py-1 text-sm rounded border ${getRoleBadgeColor(invitation.role)}`}>
                      {invitation.role}
                    </span>
                    <span className="px-3 py-1 text-sm rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                      Pending
                    </span>
                    <button
                      onClick={() => handleDeleteInvitation(invitation.id)}
                      className="px-3 py-1 bg-red-500/20 text-red-400 text-sm rounded border border-red-500/30 hover:bg-red-500/30 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* INVITE MODAL - THIS IS THE MISSING PART */}
        {showInviteModal && (
          <div 
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" 
            onClick={() => setShowInviteModal(false)}
          >
            <div 
              className="bg-cyber-card border border-gray-800 rounded-lg p-8 max-w-md w-full" 
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-white mb-6">Invite Team Member</h2>
              
              <form onSubmit={handleInviteMember} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@company.com"
                    required
                    className="w-full px-4 py-2 bg-cyber-darker border border-gray-700 rounded-lg text-white focus:border-cyber-cyan focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Role</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="w-full px-4 py-2 bg-cyber-darker border border-gray-700 rounded-lg text-white focus:border-cyber-cyan focus:outline-none"
                  >
                    <option value="admin">Admin - Can manage team and settings</option>
                    <option value="member">Member - Can view and edit data</option>
                    <option value="viewer">Viewer - Read-only access</option>
                  </select>
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-2 rounded">
                    {error}
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteModal(false);
                      setError('');
                      setInviteEmail('');
                      setInviteRole('member');
                    }}
                    className="flex-1 px-6 py-3 bg-gray-500/20 text-gray-400 font-semibold rounded border border-gray-500/30 hover:bg-gray-500/30 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-cyber-cyan text-cyber-darker font-semibold rounded hover:bg-cyan-400 transition-colors"
                  >
                    Send Invitation
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManagement;